import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, Search, Globe, FileText, Tag, Bot, ChevronDown, ChevronUp, AlertCircle, CheckCircle } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'

interface PageSEO {
  id: string
  page_key: string
  page_name: string
  meta_title: string
  meta_description: string
  meta_keywords: string
  og_title: string
  og_description: string
  og_image: string
  canonical_url: string
  robots: string
  structured_data: Record<string, unknown> | null
}

const robotsOptions = [
  { value: 'index, follow', label: 'Index, Follow (Recommended)' },
  { value: 'index, nofollow', label: 'Index, No Follow' },
  { value: 'noindex, follow', label: 'No Index, Follow' },
  { value: 'noindex, nofollow', label: 'No Index, No Follow' },
]

export function SEOPage() {
  const queryClient = useQueryClient()
  const [expandedPage, setExpandedPage] = useState<string | null>('home')
  const [editedPages, setEditedPages] = useState<Record<string, PageSEO>>({})

  const { data: pages, isLoading } = useQuery({
    queryKey: ['admin-seo-pages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('page_seo')
        .select('*')
        .order('page_key')
      if (error) throw error
      return data as PageSEO[]
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (page: PageSEO) => {
      const { error } = await supabase
        .from('page_seo')
        .update({
          meta_title: page.meta_title,
          meta_description: page.meta_description,
          meta_keywords: page.meta_keywords,
          og_title: page.og_title,
          og_description: page.og_description,
          og_image: page.og_image,
          canonical_url: page.canonical_url,
          robots: page.robots,
          structured_data: page.structured_data,
          updated_at: new Date().toISOString(),
        })
        .eq('id', page.id)
      if (error) throw error
    },
    onSuccess: (_, page) => {
      queryClient.invalidateQueries({ queryKey: ['admin-seo-pages'] })
      // Remove from edited pages after successful save
      setEditedPages((prev) => {
        const next = { ...prev }
        delete next[page.page_key]
        return next
      })
    },
  })

  const getPageData = (pageKey: string): PageSEO | undefined => {
    return editedPages[pageKey] || pages?.find((p) => p.page_key === pageKey)
  }

  const updatePageField = (pageKey: string, field: keyof PageSEO, value: string) => {
    const currentPage = getPageData(pageKey)
    if (!currentPage) return
    setEditedPages((prev) => ({
      ...prev,
      [pageKey]: { ...currentPage, [field]: value },
    }))
  }

  const isPageModified = (pageKey: string) => !!editedPages[pageKey]

  const getCharCount = (text: string, max: number) => {
    const count = text?.length || 0
    return {
      count,
      isOver: count > max,
      isOptimal: count > 0 && count <= max,
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-secondary-200 rounded w-48" />
          <div className="h-64 bg-secondary-200 rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-secondary-900 mb-2">SEO Management</h1>
        <p className="text-secondary-600">
          Manage meta tags, keywords, and search engine optimization settings for each page.
        </p>
      </div>

      {/* SEO Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <Search className="w-4 h-4" />
          SEO Best Practices
        </h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Meta Title:</strong> 50-60 characters, include primary keyword</li>
          <li>• <strong>Meta Description:</strong> 150-160 characters, compelling call-to-action</li>
          <li>• <strong>Keywords:</strong> 5-10 relevant keywords, comma-separated</li>
          <li>• <strong>OG Tags:</strong> Optimizes social media sharing appearance</li>
        </ul>
      </div>

      {/* Pages List */}
      <div className="space-y-4">
        {pages?.map((page) => {
          const pageData = getPageData(page.page_key)
          if (!pageData) return null

          const isExpanded = expandedPage === page.page_key
          const isModified = isPageModified(page.page_key)
          const titleCount = getCharCount(pageData.meta_title, 60)
          const descCount = getCharCount(pageData.meta_description, 160)

          return (
            <div
              key={page.id}
              className={cn(
                'border rounded-xl overflow-hidden transition-all',
                isExpanded ? 'border-primary-300 shadow-md' : 'border-secondary-200',
                isModified && 'ring-2 ring-yellow-400'
              )}
            >
              {/* Header */}
              <button
                onClick={() => setExpandedPage(isExpanded ? null : page.page_key)}
                className="w-full flex items-center justify-between p-4 bg-white hover:bg-secondary-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-primary-600" />
                  <div className="text-left">
                    <h3 className="font-semibold text-secondary-900">{page.page_name}</h3>
                    <p className="text-sm text-secondary-500">/{page.page_key === 'home' ? '' : page.page_key}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {isModified && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                      Unsaved changes
                    </span>
                  )}
                  {titleCount.isOptimal && descCount.isOptimal ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-500" />
                  )}
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-secondary-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-secondary-400" />
                  )}
                </div>
              </button>

              {/* Content */}
              {isExpanded && (
                <div className="border-t border-secondary-100 p-6 bg-secondary-50 space-y-6">
                  {/* Meta Title */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-secondary-700 mb-2">
                      <FileText className="w-4 h-4" />
                      Meta Title
                      <span className={cn(
                        'ml-auto text-xs',
                        titleCount.isOver ? 'text-red-500' : titleCount.isOptimal ? 'text-green-500' : 'text-secondary-400'
                      )}>
                        {titleCount.count}/60
                      </span>
                    </label>
                    <input
                      type="text"
                      value={pageData.meta_title || ''}
                      onChange={(e) => updatePageField(page.page_key, 'meta_title', e.target.value)}
                      className="input w-full"
                      placeholder="Page title for search engines..."
                    />
                  </div>

                  {/* Meta Description */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-secondary-700 mb-2">
                      <FileText className="w-4 h-4" />
                      Meta Description
                      <span className={cn(
                        'ml-auto text-xs',
                        descCount.isOver ? 'text-red-500' : descCount.isOptimal ? 'text-green-500' : 'text-secondary-400'
                      )}>
                        {descCount.count}/160
                      </span>
                    </label>
                    <textarea
                      value={pageData.meta_description || ''}
                      onChange={(e) => updatePageField(page.page_key, 'meta_description', e.target.value)}
                      className="input w-full h-24 resize-none"
                      placeholder="Brief description for search results..."
                    />
                  </div>

                  {/* Meta Keywords */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-secondary-700 mb-2">
                      <Tag className="w-4 h-4" />
                      Meta Keywords
                    </label>
                    <textarea
                      value={pageData.meta_keywords || ''}
                      onChange={(e) => updatePageField(page.page_key, 'meta_keywords', e.target.value)}
                      className="input w-full h-20 resize-none"
                      placeholder="keyword1, keyword2, keyword3..."
                    />
                    <p className="text-xs text-secondary-500 mt-1">
                      Comma-separated list of relevant keywords
                    </p>
                  </div>

                  {/* Open Graph Section */}
                  <div className="pt-4 border-t border-secondary-200">
                    <h4 className="font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Open Graph (Social Media)
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          OG Title
                        </label>
                        <input
                          type="text"
                          value={pageData.og_title || ''}
                          onChange={(e) => updatePageField(page.page_key, 'og_title', e.target.value)}
                          className="input w-full"
                          placeholder="Title for social sharing..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          OG Image URL
                        </label>
                        <input
                          type="text"
                          value={pageData.og_image || ''}
                          onChange={(e) => updatePageField(page.page_key, 'og_image', e.target.value)}
                          className="input w-full"
                          placeholder="https://..."
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        OG Description
                      </label>
                      <textarea
                        value={pageData.og_description || ''}
                        onChange={(e) => updatePageField(page.page_key, 'og_description', e.target.value)}
                        className="input w-full h-20 resize-none"
                        placeholder="Description for social sharing..."
                      />
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <div className="pt-4 border-t border-secondary-200">
                    <h4 className="font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                      <Bot className="w-4 h-4" />
                      Advanced Settings
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Canonical URL
                        </label>
                        <input
                          type="text"
                          value={pageData.canonical_url || ''}
                          onChange={(e) => updatePageField(page.page_key, 'canonical_url', e.target.value)}
                          className="input w-full"
                          placeholder="https://yourdomain.com/page"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Robots
                        </label>
                        <select
                          value={pageData.robots || 'index, follow'}
                          onChange={(e) => updatePageField(page.page_key, 'robots', e.target.value)}
                          className="input w-full"
                        >
                          {robotsOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="pt-4 border-t border-secondary-200">
                    <h4 className="font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                      <Search className="w-4 h-4" />
                      Search Preview
                    </h4>
                    <div className="bg-white rounded-lg p-4 border border-secondary-200">
                      <div className="text-blue-600 text-lg hover:underline cursor-pointer truncate">
                        {pageData.meta_title || 'Page Title'}
                      </div>
                      <div className="text-green-700 text-sm truncate">
                        https://engimarinex.com/{page.page_key === 'home' ? '' : page.page_key}
                      </div>
                      <div className="text-secondary-600 text-sm mt-1 line-clamp-2">
                        {pageData.meta_description || 'Page description will appear here...'}
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="pt-4 flex justify-end">
                    <button
                      onClick={() => saveMutation.mutate(pageData)}
                      disabled={!isModified || saveMutation.isPending}
                      className={cn(
                        'btn-primary flex items-center gap-2',
                        !isModified && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <Save className="w-4 h-4" />
                      {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Empty State */}
      {(!pages || pages.length === 0) && (
        <div className="text-center py-12 bg-secondary-50 rounded-xl">
          <Search className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-secondary-900 mb-2">No SEO Data Found</h3>
          <p className="text-secondary-600">
            Please run the database migration to initialize SEO settings for all pages.
          </p>
        </div>
      )}
    </div>
  )
}
